import { ClassicPreset as Classic } from 'rete';
import { api } from '../../../../api';
import type {
  Id,
  Task,
  TaskApprovalGuards,
  TaskDefinition,
} from '../../../../api/types';
import { normalizeTaskApprovalGuards } from './approval-guards';
import { LabeledInputControl } from './plugins';

export const socket = new Classic.Socket('socket');

export class TaskNode extends Classic.Node {
  width = 200;

  get height(): number {
    return (
      64 + // title
      (this.taskIcon ? 80 : 0) + // icon
      Object.keys(this.inputs ?? {}).length * 36 + // input height
      Object.keys(this.outputs ?? {}).length * 36
    ); // output height
  }

  private workflowId?: Id;
  private propagateConfigUpdates = true;
  public taskType: string;
  public config: Record<string, unknown> = {};
  public taskIcon?: string;
  public taskLabel?: string | null;
  public approvalGuards: TaskApprovalGuards = {
    preExecution: false,
    postExecution: false,
  };

  constructor(
    task: Task,
    taskDefinitions: TaskDefinition[],
    workflowId: Id,
    taskIcon?: string
  ) {
    // Find the matching task definition by type
    const taskDefinition = taskDefinitions.find(
      (def) => def.name === task.type
    );

    super(taskDefinition?.name ?? `Task ${task.id}`);
    // Align the node id with the task id so API interactions use correct identifiers
    this.id = String(task.id);
    this.workflowId = workflowId;

    const inputs = taskDefinition?.inputDescription;
    const outputs = taskDefinition?.outputDescription;
    const configDesc = taskDefinition?.configDescription;
    const configValues = (task.config as any)?.data ?? task.config ?? {};

    this.taskType = task.type;
    this.config = { ...configValues };
    if (taskIcon) this.taskIcon = taskIcon;
    this.taskLabel = task.label ?? null;
    this.approvalGuards = normalizeTaskApprovalGuards(task.approvalGuards);

    if (inputs) {
      for (const [key, desc] of Object.entries(inputs)) {
        const input = new Classic.Input(
          socket,
          desc.name,
          desc.types.join().includes('array')
        );
        (input as any).value = undefined;
        this.addInput(key, input);
      }
    }
    if (outputs) {
      for (const [key, desc] of Object.entries(outputs)) {
        const output = new Classic.Output(socket, (desc as any).name ?? key);
        (output as any).value = undefined;
        this.addOutput(key, output);
      }
    }
    if (configDesc) {
      const isVisible = (
        desc: any,
        config: Record<string, unknown>
      ): boolean => {
        const visibility = desc?.visibility as
          | Array<{
              show?: Record<string, unknown>;
              hide?: Record<string, unknown>;
            }>
          | undefined;
        if (!visibility || visibility.length === 0) return true;
        return visibility.some((rule) => {
          if (rule.show) {
            for (const [k, allowed] of Object.entries(rule.show)) {
              const allowedValues = Array.isArray(allowed)
                ? allowed
                : [allowed];
              if (!allowedValues.includes((config as any)?.[k])) return false;
            }
          }
          if (rule.hide) {
            for (const [k, disallowed] of Object.entries(rule.hide)) {
              const disallowedValues = Array.isArray(disallowed)
                ? disallowed
                : [disallowed];
              if (disallowedValues.includes((config as any)?.[k])) return false;
            }
          }
          return true;
        });
      };

      for (const [key, desc] of Object.entries(configDesc)) {
        if (!isVisible(desc, this.config)) continue;

        const initial = configValues[key] ?? (desc as any).defaultValue ?? '';
        const types = (desc as any).types as string[] | undefined;
        const label = (desc as any).name ?? key;

        const control = new LabeledInputControl('text', {
          initial: String(initial),
          change: async (val: string) => {
            let parsed: unknown = val;
            if (types?.includes('boolean')) {
              parsed = val === 'true';
            } else if (types?.includes('int') || types?.includes('float')) {
              const num = Number(val);
              parsed = Number.isNaN(num) ? val : num;
            }
            this.config[key] = parsed;
            if (this.propagateConfigUpdates && this.workflowId) {
              try {
                await api.workflows.updateTask(
                  this.workflowId,
                  Number(this.id),
                  {
                    config: { [key]: parsed },
                  }
                );
              } catch (e) {
                console.error(e);
              }
            }
          },
        });
        control.label = label;
        this.addControl(key, control);
      }
    }

    // Initialize state values for UI
    (this as any).error = (task.state as any)?.error;
    (this as any).status = (task.state as any)?.status;
    const initialInput =
      (task.state as any)?.input?.data ?? (task.state as any)?.input;
    const initialOutput =
      (task.state as any)?.output?.data ?? (task.state as any)?.output;
    this.updateValues(initialInput, initialOutput);
    (this as any).disabled = (task.state as any)?.disabled ?? false;
  }

  public updateLabel(label: string | null) {
    this.taskLabel = label ?? null;
  }

  public updateApprovalGuards(approvalGuards: unknown) {
    this.approvalGuards = normalizeTaskApprovalGuards(approvalGuards);
  }

  public updateConfig(config: Record<string, unknown>): string[] {
    const controls = this.controls as Record<string, any>;
    const controlIds = [];
    for (const [key, value] of Object.entries(config ?? {})) {
      const control = controls[key];
      if (control && typeof control.setValue === 'function') {
        this.propagateConfigUpdates = false;
        controlIds.push(control.id);
        control.setValue(String(value));
        this.config[key] = value;
        this.propagateConfigUpdates = true;
      }
    }
    return controlIds;
  }

  public updateValues(
    input?: Record<string, unknown>,
    output?: Record<string, unknown>
  ): string[] {
    const updatedSocketIds: string[] = [];

    if (input) {
      for (const [key, value] of Object.entries(input)) {
        const inp = this.inputs[key];
        if (inp) {
          // Create a new value object to ensure Lit detects the change
          (inp as any).socket.value = value;
          (inp as any).value = value;
          updatedSocketIds.push(inp.id);
        }
      }
    }
    if (output) {
      for (const [key, value] of Object.entries(output)) {
        const out = this.outputs[key];
        if (out) {
          (out as any).socket.value = value;
          (out as any).value = value;
          updatedSocketIds.push(out.id);
        }
      }
    }

    return updatedSocketIds;
  }
}
